import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PackagingItemListResponse } from '@produzionejb/types';
import { api } from '../lib/api-client';
import { PopoverPortal } from './PopoverPortal';

function usePackagingItems(search: string) {
  return useQuery({
    queryKey: ['packaging-items', search],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (search.trim()) qs.set('search', search.trim());
      qs.set('active', 'true');
      qs.set('pageSize', '50');
      const s = qs.toString();
      return api<PackagingItemListResponse>(`/api/packaging-items${s ? `?${s}` : ''}`);
    },
    staleTime: 30_000,
  });
}

export function PackagingItemPicker({
  value: _value,
  valueName,
  valueCategory,
  onChange,
  disabled,
}: {
  value: string;
  valueName?: string;
  valueCategory?: string;
  onChange: (id: string, name: string, category: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [search, setSearch] = useState('');
  const itemsQ = usePackagingItems(search);
  const display = valueName ? `${valueName}${valueCategory ? ' (' + valueCategory + ')' : ''}` : '';
  return (
    <div className="relative">
      <button
        ref={anchorRef}
        type="button"
        className="w-full rounded-md border border-slate-300 px-2 py-1 text-left text-xs"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
      >
        {display || '— scegli packaging —'}
      </button>
      <PopoverPortal anchorRef={anchorRef} open={open} onClose={() => setOpen(false)}>
        <div className="w-96 rounded-md border border-slate-300 bg-white p-2 shadow-lg">
          <input
            autoFocus
            type="text"
            className="mb-2 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
            placeholder="Cerca packaging..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul className="max-h-72 overflow-y-auto text-xs">
            {itemsQ.isLoading && <li className="p-1 text-slate-400">Caricamento...</li>}
            {itemsQ.data && itemsQ.data.items.length === 0 && (
              <li className="p-1 text-slate-400">Nessun risultato</li>
            )}
            {itemsQ.data?.items.map((it) => (
              <li
                key={it.id}
                className="cursor-pointer rounded p-1 hover:bg-slate-100"
                onClick={() => {
                  onChange(it.id, it.name, it.category);
                  setOpen(false);
                  setSearch('');
                }}
              >
                <span className="font-medium">{it.name}</span>
                <span className="ml-2 text-slate-500">{it.category}</span>
                <span className="ml-2 text-slate-400 font-mono">{it.code}</span>
              </li>
            ))}
          </ul>
        </div>
      </PopoverPortal>
    </div>
  );
}
